import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router';
import { shallow as shallowEqual } from 'zustand/shallow';

type SetState<T> = Partial<T> | ((prev: T) => Partial<T>);
type Selector<T, U> = (state: T) => U;
type Listener = () => void;
type EqualityFn<U> = (a: U, b: U) => boolean;

const parseValue = <T>(value: string | null, defaultValue: T): T => {
  if (value === null) {
    return defaultValue;
  }

  try {
    if (typeof defaultValue === 'number') {
      return Number(value) as T;
    }

    if (typeof defaultValue === 'boolean') {
      return (value === 'true') as T;
    }

    if (typeof defaultValue === 'object') {
      return JSON.parse(value) as T;
    }

    return value as T;
  } catch {
    return value as T;
  }
};

const stringifyValue = (value: unknown): string => {
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

export function createUrlStore<T extends Record<string, unknown>>(
  init: (set: (s: SetState<T>) => void, get: () => T) => T
) {
  const defaults = init(
    () => {},
    () => defaults
  );

  function useParamStore<U = T>(
    selector?: Selector<T, U>,
    equality?: EqualityFn<U>
  ): U {
    const [searchParams, setSearchParams] = useSearchParams();
    const listeners = useRef(new Set<Listener>()).current;
    const stateRef = useRef<T>(defaults);
    const sel = selector ?? ((s: T) => s as unknown as U);
    const lastSelectedRef = useRef<U>(sel(stateRef.current));

    // Parse URL to state
    const parseUrl = useCallback((): T => {
      const state = {} as T;

      for (const key of Object.keys(defaults)) {
        // Only parse serializable state keys (skip functions/actions)
        if (typeof (defaults as any)[key] === 'function') {
          continue;
        }

        const value = searchParams.get(key);
        (state as any)[key] = parseValue(value, defaults[key as keyof T]);
      }

      return state;
    }, [searchParams]);

    stateRef.current = parseUrl();

    // Set function updates URL via React Router
    const set = useCallback(
      (action: SetState<T>) => {
        const current = stateRef.current;
        const updates = typeof action === 'function' ? action(current) : action;
        const next = { ...current, ...updates };
        stateRef.current = next;

        setSearchParams(
          prev => {
            const params = new URLSearchParams(prev);

            for (const k of Object.keys(defaults)) {
              // Only sync serializable state keys (skip functions/actions)
              if (typeof (defaults as any)[k] === 'function') {
                continue;
              }

              const v = (next as any)[k];

              if (v == null || v === defaults[k as keyof T]) {
                params.delete(k); // Remove default values from URL
              } else {
                params.set(k, stringifyValue(v));
              }
            }

            return params;
          },
          { replace: true }
        );

        listeners.forEach(l => l());
      },
      [setSearchParams]
    );

    // Build state with actions
    const stateWithActions = init(set, () => stateRef.current);

    {
      const parsed = parseUrl();
      stateRef.current = { ...stateWithActions, ...parsed };
    }

    const subscribe = useCallback((listener: Listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }, []);

    const getSnapshot = useCallback(() => {
      const selected = sel(stateRef.current);
      const eq = equality ?? shallowEqual;

      if (eq(lastSelectedRef.current, selected)) {
        return lastSelectedRef.current;
      }

      lastSelectedRef.current = selected;
      return selected;
    }, [sel, equality]);

    // Sync when URL changes externally (back/forward)
    useEffect(() => {
      stateRef.current = { ...stateRef.current, ...parseUrl() };
      listeners.forEach(l => l());
    }, [searchParams, parseUrl]);

    return useSyncExternalStore(subscribe, getSnapshot);
  }

  return { useParamStore };
}
